import {
	CustomerListInput,
	type CustomerListItem,
	type CustomerListQuery,
} from "@cleanbudget/contract/customer";
import { BusinessProfileStatus } from "@cleanbudget/contract/customer-account";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { formatDate } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { AccountBadge, BusinessStatusBadge, CustomerName } from "./-components/customer-badges";

const customersQuery = (query: CustomerListQuery) => orpc.customer.list.queryOptions({ input: query });
const groupsQuery = () => orpc.price.listCustomerGroups.queryOptions();

/** The list's defaults, left out of the URL so a plain `/customer` is the default view. */
const { sort, order, page, pageSize } = CustomerListInput.parse({});

/**
 * Klanten (#76): guests and account holders in one data table. Search (name, email, company), the
 * consumer/business and customer group filters, sort and page live in the URL.
 */
export const Route = createFileRoute("/_admin/customer/")({
	validateSearch: CustomerListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) =>
		Promise.all([
			context.queryClient.ensureQueryData(customersQuery(deps)),
			context.queryClient.ensureQueryData(groupsQuery()),
		]),
	component: CustomersPage,
});

function CustomersPage() {
	const { t } = useTranslation("customer");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(customersQuery(search));
	const { data: groups } = useSuspenseQuery(groupsQuery());
	const businessGroups = groups.groups.filter((group) => group.kind === "business");

	const columns: readonly Column<CustomerListItem>[] = [
		{
			id: "name",
			header: t("column.customer"),
			sortable: true,
			cell: (customer) => (
				<Link
					to="/customer/$customerId"
					params={{ customerId: customer.id }}
					className="flex flex-col hover:underline"
				>
					<CustomerName customer={customer} />
					<span className="text-xs text-muted-foreground">{customer.email}</span>
				</Link>
			),
		},
		{
			id: "company",
			header: t("column.company"),
			secondary: true,
			cell: (customer) =>
				customer.companyName ? (
					<span className="flex flex-wrap items-center gap-2">
						{customer.companyName}
						{customer.businessStatus ? <BusinessStatusBadge status={customer.businessStatus} /> : null}
					</span>
				) : null,
		},
		{
			id: "kind",
			header: t("column.kind"),
			cell: (customer) => t(`kind.${customer.kind}`),
		},
		{
			id: "group",
			header: t("column.group"),
			secondary: true,
			cell: (customer) => customer.customerGroup?.name ?? null,
		},
		{
			id: "account",
			header: t("column.account"),
			secondary: true,
			cell: (customer) => <AccountBadge customer={customer} />,
		},
		{
			id: "createdAt",
			header: t("column.created"),
			sortable: true,
			secondary: true,
			cell: (customer) => <time dateTime={customer.createdAt}>{formatDate(customer.createdAt)}</time>,
		},
	];

	const filtered =
		Boolean(search.search) ||
		search.kind !== undefined ||
		search.businessStatus !== undefined ||
		search.customerGroupId !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title={t("title")} description={t("description")} />
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="kind"
						label={t("kind.label")}
						value={search.kind}
						options={[
							{ value: "consumer", label: t("kind.consumer") },
							{ value: "business", label: t("kind.business") },
						]}
					/>
					<FilterSelect
						param="businessStatus"
						label={t("businessStatus.label")}
						value={search.businessStatus}
						options={BusinessProfileStatus.options.map((status) => ({
							value: status,
							label: t(`businessStatus.${status}`),
						}))}
					/>
					{businessGroups.length > 0 ? (
						<FilterSelect
							param="customerGroupId"
							label={t("groupFilter")}
							value={search.customerGroupId}
							options={businessGroups.map((group) => ({ value: group.id, label: group.name }))}
						/>
					) : null}
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(customer) => customer.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
